import { HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CityService, CountryService } from '@pulpo/pulpo-api';
import { clearPossibleSubscriptions, sortByName } from '@pulpo/pulpo-commons';
import { ICity, ICountry, IDestination, City } from '@pulpo/pulpo-models';
import { Observable, Subscription } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-city-edit',
  templateUrl: './city-edit.component.html',
  styleUrls: ['./city-edit.component.scss'],
})
export class CityEditComponent implements OnInit, OnDestroy {
  editMode: boolean;
  city?: ICity;
  editForm: FormGroup;
  countries: ICountry[];
  destinations: IDestination[];
  selectedDestinationIds: number[];
  destinationSelectControl = new FormControl<number | null>(null);
  isSaving: boolean;
  resultSubscription: Subscription;
  sortByName = sortByName;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<CityEditComponent>,
    private cityService: CityService,
    private countryService: CountryService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.city = data.city;
    this.destinations = data.destinations ?? [];
    this.selectedDestinationIds = data.city?.destinationIds ? [...data.city.destinationIds] : [];
  }

  ngOnInit() {
    this.editMode = !!this.city;
    this.isSaving = false;

    this.countryService.query().subscribe({
      next: (res) => {
        this.countries = <ICountry[]>res.body;
        this.initForm();
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  ngOnDestroy(): void {
    clearPossibleSubscriptions(this.resultSubscription);
  }

  private initForm(): void {
    if (this.editMode) {
      this.initUpdateForm();
    } else {
      this.initCreationForm();
    }
  }

  private initUpdateForm(): void {
    this.editForm = this.fb.group({
      id: [this.city?.id],
      name: [this.city?.name],
      country: [this.city?.country ? this.city.country.id : null],
    });
  }

  private initCreationForm(): void {
    this.editForm = this.fb.group({
      name: [],
      country: [],
    });
  }

  onSubmitForm(): void {
    this.isSaving = true;
    const city = this.getCreatedOrUpdatedCity();

    if (this.editMode) {
      this.subscribeToSaveResponse(this.cityService.update(city));
    } else {
      this.subscribeToSaveResponse(this.cityService.create(city));
    }
  }

  addDestination(id: number | null): void {
    if (id !== null && !this.selectedDestinationIds.includes(id)) {
      this.selectedDestinationIds.push(id);
      this.destinationSelectControl.setValue(null);
      this.editForm.markAsDirty();
    }
  }

  removeDestination(id: number): void {
    this.selectedDestinationIds = this.selectedDestinationIds.filter((x) => x !== id);
    this.editForm.markAsDirty();
  }

  getAvailableDestinations(): IDestination[] {
    return this.destinations.filter(
      (d) => d.id !== undefined && !this.selectedDestinationIds.includes(d.id!)
    );
  }

  getDestinationName(id: number): string {
    return this.destinations.find((d) => d.id === id)?.name ?? String(id);
  }

  private getCreatedOrUpdatedCity(): ICity {
    const { id, name } = this.editForm.value;
    const country = this.countries.find(
      (c) => c.id === this.editForm.value.country
    );

    return {
      ...new City(),
      id,
      name,
      country: country ? country : {},
      destinationIds: this.selectedDestinationIds,
    };
  }

  private subscribeToSaveResponse(result: Observable<HttpResponse<ICity>>) {
    clearPossibleSubscriptions(this.resultSubscription);

    this.resultSubscription = result.subscribe({
      next: (res) => {
        this.isSaving = false;
        this.dialogRef.close(res.body);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }
}
